/**
 * **A day's openings** — what already holds the day and which whole hours are still free, for a
 * session being placed on it. One reading for both places that place a session by hand: the series
 * window's mover (an occurrence among its siblings) and "Przełóż" (calendar-management R9: the
 * reschedule shows the chosen day's occupied and free slots).
 *
 * The session being moved is not in its own way: `exceptId` takes it out of "occupied" and out of
 * what a free hour must clear.
 *
 * Pure: no React, no DOM, no query hook, no i18next.
 */

import type { BookingSession } from './bookingSession';
import { blockedSpansOn, sessionsHoldingOn, sessionsOverlapping } from './busyTime';
import { zonedInstant, type CivilDay } from './trainerClock';
import type { TrainerBlock, WorkingDay } from './workingHours';

const MINUTE_MS = 60_000;

/**
 * The whole hours of `day` a session could take without colliding: inside the trainer's working
 * windows (or the grid's opening hours where the day has none), clear of every session and block,
 * and still ahead of `nowMs`. A series' own other occurrences do not take an hour away: the trainer
 * may put two of them on one hour (owner, 25 September 2026) — the series list marks the pair as a
 * conflict and never blocks it (recurring-sessions R9).
 */
export function freeHoursOn(
  day: CivilDay,
  context: {
    readonly zone: string;
    readonly nowMs: number;
    readonly durationMinutes: number;
    readonly working: WorkingDay | null;
    readonly blocks: readonly TrainerBlock[];
    readonly sessions: readonly BookingSession[];
    /** Where the grid opens and closes, for a day with no windows. */
    readonly fallback: { readonly start: number; readonly end: number };
  },
): number[] {
  const windows =
    context.working !== null && context.working.windows.length > 0
      ? context.working.windows
      : [context.fallback];
  const blocked = blockedSpansOn(context.blocks, day, context.zone);
  const hours: number[] = [];
  for (const window of windows) {
    for (
      let minutes = Math.ceil(window.start / 60) * 60;
      minutes + context.durationMinutes <= window.end;
      minutes += 60
    ) {
      if (minutes === 0) continue;
      const startMs = zonedInstant(day, minutes, context.zone);
      const span = { startMs, endMs: startMs + context.durationMinutes * MINUTE_MS };
      const end = minutes + context.durationMinutes;
      const clear =
        startMs >= context.nowMs &&
        sessionsOverlapping(context.sessions, span).length === 0 &&
        !blocked.some((block) => block.start < end && minutes < block.end);
      if (clear && !hours.includes(minutes)) hours.push(minutes);
    }
  }
  return hours.sort((a, b) => a - b);
}

/** What a day offers a session being placed on it. */
export interface DayOpenings {
  /** Sessions holding an hour that day, the moved one excepted. */
  readonly occupied: readonly BookingSession[];
  /** Whole hours a session of this length could start at. */
  readonly free: readonly number[];
}

export function dayOpenings(
  day: CivilDay,
  context: Parameters<typeof freeHoursOn>[1] & { readonly exceptId: number | null },
): DayOpenings {
  const sessions = context.sessions.filter((session) => session.id !== context.exceptId);
  return {
    occupied: sessionsHoldingOn(sessions, day),
    free: freeHoursOn(day, { ...context, sessions }),
  };
}

/** Whether a start at `minutes` on `day` runs into what already holds the day. */
export function isOccupiedAt(
  openings: DayOpenings,
  day: CivilDay,
  minutes: number,
  durationMinutes: number,
  zone: string,
): boolean {
  const startMs = zonedInstant(day, minutes, zone);
  return (
    sessionsOverlapping(openings.occupied, {
      startMs,
      endMs: startMs + durationMinutes * MINUTE_MS,
    }).length > 0
  );
}
